import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { Operator } from '@img-arena/consoles-api-streaming'
import { Checkbox, Divider, FormControlLabel, Stack } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FileUploadInput } from '@/components/inputs/FileUploadInput'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { InputSectionDivider } from '@/components/inputs/InputSectionDivider'
import { PhoneNumberInput } from '@/components/inputs/PhoneNumberInput'
import { streamingRestApi } from '@/service/streamingApi'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { onFieldChange } from '@/utils/forms/formik'

import { DGE_OPERATOR_GROUP } from './OperatorForm.constants'
import { operatorFormValidationSchema } from './operatorForm.validation'

export type DGE_OperatorFormikValues = {
  operatorName: string
  contactName: string
  syndicationOperator: boolean
  email: string
  contactNumber: string
  maxTraders: number
  pricePerGB: number
  shieldClientId: string
  shieldClientAccessKey: string
  shieldClientSecret: string
  mainNotificationEmail: string
  analyticsNotificationEmail: string
  licenseLimitNotificationEmail: string
  operatorLogo: Blob | null
}

export type DGE_OperatorFormProps = FormDialogProps<
  Operator,
  undefined,
  keyof DGE_OperatorFormikValues
>

export const DGEOperatorForm = ({
  onClose,
  onSubmit,
  open = false,
  defaultValues
}: DGE_OperatorFormProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const isEditMode = !!defaultValues?.id

  /* NOTE: DGE does not allow us to upload operator logo yet
          let logoUrl: string | null = null

          if (values.operatorLogo) {
            const formData = new FormData()
            formData.append('file', values.operatorLogo)

            const { data } = await request(
              'https://dge-exchange.streams-dev.srarena.io/upload-object',
              {
                method: 'POST',
                data: formData,
                headers: {
                  'Content-Type': 'multipart/form-data'
                },
                platform: Platform.DGE
              }
            )

            if (typeof data === 'string') {
              logoUrl = data
            } else {
              enqueueSnackbar('Failed to upload operator logo', { variant: 'error' })
            }
          }
        */

  const formik = useFormik<DGE_OperatorFormikValues>({
    initialValues: {
      operatorName: defaultValues?.name || '',
      contactName: defaultValues?.mainContactName || '',
      syndicationOperator: defaultValues?.isPush || false,
      email: defaultValues?.mainContactEmail || '',
      contactNumber: defaultValues?.mainContactTel || '',
      maxTraders: defaultValues?.maxTraders ?? 0,
      pricePerGB: defaultValues?.pricePerGB ?? 0,
      shieldClientId: defaultValues?.shieldClientId || '',
      shieldClientAccessKey: defaultValues?.shieldClientAccessKey || '',
      shieldClientSecret: defaultValues?.shieldClientSecretKey || '',
      mainNotificationEmail: defaultValues?.notificationEmails || '',
      analyticsNotificationEmail: defaultValues?.mailingEmails || '',
      licenseLimitNotificationEmail: defaultValues?.licenseLimitEmails || '',
      operatorLogo: null
    },

    enableReinitialize: true,
    validationSchema: operatorFormValidationSchema.DGE,

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.operatorName) return

      try {
        setSubmitting(true)

        if (isEditMode && defaultValues?.id) {
          const updatedOperator: Operator = {
            id: defaultValues.id,
            name: values.operatorName,
            logoUrl: defaultValues.logoUrl ?? null,
            mainContactName: values.contactName,
            mainContactTel: values.contactNumber,
            mainContactEmail: values.email,
            maxTraders: values.maxTraders,
            createdAt: defaultValues.createdAt ?? '',
            notificationEmails: values.mainNotificationEmail,
            pricePerGB: values.pricePerGB,
            isPush: values.syndicationOperator,
            mailingEmails: values.analyticsNotificationEmail,
            licenseLimitEmails: values.licenseLimitNotificationEmail,
            group: defaultValues.group ?? '',
            shieldClientId: values.shieldClientId,
            shieldClientAccessKey: values.shieldClientAccessKey,
            shieldClientSecretKey: values.shieldClientSecret
          }
          await streamingRestApi.endpoints.operators.edit(updatedOperator)
          onSubmit?.()
          enqueueSnackbar(`Operator ${values.operatorName} has been updated`, {
            variant: 'success'
          })
        } else {
          await streamingRestApi.endpoints.operators.add({
            group: DGE_OPERATOR_GROUP,
            isPush: values.syndicationOperator,
            licenseLimitEmails: values.licenseLimitNotificationEmail,
            notificationEmails: values.mainNotificationEmail,
            mailingEmails: values.analyticsNotificationEmail,
            name: values.operatorName,
            mainContactEmail: values.email,
            mainContactName: values.contactName,
            mainContactTel: values.contactNumber,
            maxTraders: values.maxTraders,
            pricePerGB: values.pricePerGB,
            logoUrl: null,
            shieldClientAccessKey: values.shieldClientAccessKey,
            shieldClientId: values.shieldClientId,
            shieldClientSecretKey: values.shieldClientSecret
          })
          onSubmit?.()
          enqueueSnackbar(`Operator ${values.operatorName} has been created`, {
            variant: 'success'
          })
        }
        setSubmitting(false)
        handleClose()
      } catch (e) {
        enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} operator`, {
          variant: 'error'
        })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw e
      }
    }
  })

  const handleClose = () => {
    onClose?.()
    formik.resetForm()
  }

  return (
    <ConsoleDialog
      open={open}
      title={isEditMode ? 'Edit Operator' : 'Add Operator'}
      onClose={handleClose}
      dialogSize={false}
      width={722}
      smallTopMargin
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack direction="column" flexGrow={1}>
            <FormikTextField name="operatorName" label="Operator Name" formik={formik} required />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                value={formik.values.syndicationOperator}
                onChange={(e) => onFieldChange(formik, 'syndicationOperator', e.target.checked)}
              />
            }
            label="Syndication Operator"
            sx={{
              whiteSpace: 'nowrap',
              pt: 1
            }}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Stack direction="column" width="50%">
            <InputSectionDivider label="Operator" sx={{ mt: -0.5 }} />

            <FormikTextField name="email" label="Contact Email" formik={formik} required />
            <FormikTextField name="contactName" label="Contact Name" formik={formik} />
            <PhoneNumberInput
              label="Contact Number"
              onChange={(value) => onFieldChange(formik, 'contactNumber', value)}
            />
            <FileUploadInput
              label="Operator Logo"
              disabled // NOTE: DGE does not allow us to upload operator logo yet
              disabledInfo="Streaming API does not allow logo upload from the ops-console. Please use the ALC platform to upload operator logo until this feature is implemented."
              onChange={(value) => onFieldChange(formik, 'operatorLogo', value)}
            />

            <InputSectionDivider label="Details" sx={{ pt: 1.25 }} />

            <FormikTextField
              name="maxTraders"
              label="Max Traders"
              formik={formik}
              type="number"
              required
            />
            <FormikTextField
              name="pricePerGB"
              label="Price Per GB"
              formik={formik}
              type="number"
              required
            />
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="column" width="50%">
            <InputSectionDivider label="Shield Client" sx={{ mt: -0.5 }} />

            <FormikTextField name="shieldClientId" label="ID" formik={formik} />
            <FormikTextField name="shieldClientAccessKey" label="Access Key" formik={formik} />
            <FormikTextField name="shieldClientSecret" label="Secret" formik={formik} />

            <InputSectionDivider label="Notifications" sx={{ pt: 1.25 }} />

            <FormikTextField name="mainNotificationEmail" label="Main E-mail" formik={formik} />
            <FormikTextField
              name="analyticsNotificationEmail"
              label="Analytics E-mail"
              formik={formik}
            />
            <FormikTextField
              name="licenseLimitNotificationEmail"
              label="License Limit E-mail"
              formik={formik}
            />
          </Stack>
        </Stack>
      </Stack>
    </ConsoleDialog>
  )
}
